import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'

export interface FraudAlert {
  id: string
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info'
  type: 'sequential' | 'velocity' | 'datacenter' | 'domain' | 'zip' | 'pattern'
  title: string
  message: string
  timestamp: Date
  affectedLeads: number
  data?: any
  actions?: AlertAction[]
  autoDismiss?: number
  acknowledged?: boolean
  acknowledgedBy?: string
  acknowledgedAt?: Date
}

export interface AlertAction {
  id: string
  label: string
  type: 'primary' | 'secondary' | 'destructive'
  handler: () => void
}

interface AlertConfig {
  soundEnabled: boolean
  volume: number
  notificationsEnabled: boolean
  channels: {
    banner: boolean
    toast: boolean
    browser: boolean
    email: boolean
    slack: boolean
  }
  sounds: {
    critical: string
    high: string
    medium: string
    low: string
  }
}

interface AlertContextType {
  alerts: FraudAlert[]
  activeAlerts: FraudAlert[]
  alertHistory: FraudAlert[]
  config: AlertConfig
  addAlert: (alert: Omit<FraudAlert, 'id' | 'timestamp'>) => void
  dismissAlert: (alertId: string) => void
  acknowledgeAlert: (alertId: string, userId?: string) => void
  clearAllAlerts: () => void
  updateConfig: (config: Partial<AlertConfig>) => void
  playAlertSound: (severity: FraudAlert['severity']) => void
}

const AlertContext = createContext<AlertContextType | null>(null)

export const useAlerts = () => {
  const context = useContext(AlertContext)
  if (!context) {
    throw new Error('useAlerts must be used within AlertProvider')
  }
  return context
}

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<FraudAlert[]>([])
  const [alertHistory, setAlertHistory] = useState<FraudAlert[]>([])
  const [config, setConfig] = useState<AlertConfig>({
    soundEnabled: true,
    volume: 0.7,
    notificationsEnabled: true,
    channels: {
      banner: true,
      toast: true,
      browser: true,
      email: true,
      slack: false
    },
    sounds: {
      critical: 'alarm',
      high: 'warning',
      medium: 'notification',
      low: 'chime'
    }
  })

  const activeAlerts = alerts.filter(alert => !alert.acknowledged)

  const playAlertSound = useCallback((severity: FraudAlert['severity']) => {
    if (!config.soundEnabled) return

    try {
      // Create a simple tone based on severity
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      // Set frequency based on severity
      const frequencies = {
        critical: 800,
        high: 600,
        medium: 400,
        low: 300
      }

      oscillator.frequency.setValueAtTime(frequencies[severity], audioContext.currentTime)
      gainNode.gain.setValueAtTime(config.volume * 0.1, audioContext.currentTime)

      // Play pattern based on severity
      if (severity === 'critical') {
        // Urgent beeping pattern
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.1)
        setTimeout(() => {
          const osc2 = audioContext.createOscillator()
          const gain2 = audioContext.createGain()
          osc2.connect(gain2)
          gain2.connect(audioContext.destination)
          osc2.frequency.setValueAtTime(frequencies[severity], audioContext.currentTime)
          gain2.gain.setValueAtTime(config.volume * 0.1, audioContext.currentTime)
          osc2.start(audioContext.currentTime)
          osc2.stop(audioContext.currentTime + 0.1)
        }, 200)
      } else {
        // Single tone
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.3)
      }
    } catch (error) {
      console.warn('Could not play alert sound:', error)
    }
  }, [config.soundEnabled, config.volume])

  const showBrowserNotification = useCallback((alert: FraudAlert) => {
    if (!config.notificationsEnabled || !config.channels.browser) return
    if (Notification.permission !== 'granted') return

    const notification = new Notification(`🚨 ${alert.title}`, {
      body: alert.message,
      icon: '/favicon.ico',
      tag: alert.id,
      requireInteraction: alert.severity === 'critical'
    })

    // Auto-dismiss non-critical notifications
    if (alert.severity !== 'critical') {
      setTimeout(() => notification.close(), 5000)
    }
  }, [config.notificationsEnabled, config.channels.browser])

  const addAlert = useCallback((alertData: Omit<FraudAlert, 'id' | 'timestamp'>) => {
    const alert: FraudAlert = {
      ...alertData,
      id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    }

    setAlerts(prev => [alert, ...prev.slice(0, 99)]) // Keep max 100 alerts
    setAlertHistory(prev => [alert, ...prev])

    // Play sound
    if (config.soundEnabled) {
      playAlertSound(alert.severity)
    }

    // Show toast notification
    if (config.channels.toast) {
      const toastConfig = {
        title: alert.title,
        description: alert.message,
        duration: alert.severity === 'critical' ? Infinity : 5000,
      }

      if (alert.severity === 'critical') {
        toast.error(alert.title, { description: alert.message, duration: Infinity })
      } else if (alert.severity === 'high') {
        toast.warning(alert.title, { description: alert.message })
      } else {
        toast.info(alert.title, { description: alert.message })
      }
    }

    // Show browser notification
    showBrowserNotification(alert)

    // Auto-dismiss if configured
    if (alert.autoDismiss) {
      setTimeout(() => {
        dismissAlert(alert.id)
      }, alert.autoDismiss)
    }
  }, [config, playAlertSound, showBrowserNotification])

  const dismissAlert = useCallback((alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId))
  }, [])

  const acknowledgeAlert = useCallback((alertId: string, userId = 'current-user') => {
    const now = new Date()
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId 
        ? { ...alert, acknowledged: true, acknowledgedBy: userId, acknowledgedAt: now }
        : alert
    ))
    setAlertHistory(prev => prev.map(alert => 
      alert.id === alertId 
        ? { ...alert, acknowledged: true, acknowledgedBy: userId, acknowledgedAt: now }
        : alert
    ))
  }, [])

  const clearAllAlerts = useCallback(() => {
    setAlerts([])
  }, [])

  const updateConfig = useCallback((newConfig: Partial<AlertConfig>) => {
    setConfig(prev => ({ ...prev, ...newConfig }))
  }, [])

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Simulate real-time alerts for demo
  useEffect(() => {
    const interval = setInterval(() => {
      const shouldGenerate = Math.random() < 0.1 // 10% chance every 5 seconds
      if (!shouldGenerate) return

      const alertTypes = [
        {
          severity: 'critical' as const,
          type: 'sequential' as const,
          title: 'Sequential Phone Attack Detected',
          message: 'Multiple sequential phone numbers detected from same IP range',
          affectedLeads: Math.floor(Math.random() * 10) + 5
        },
        {
          severity: 'high' as const,
          type: 'velocity' as const,
          title: 'Velocity Attack in Progress',
          message: 'High submission rate detected - possible bot attack',
          affectedLeads: Math.floor(Math.random() * 8) + 3
        },
        {
          severity: 'medium' as const,
          type: 'datacenter' as const,
          title: 'Data Center IP Detected',
          message: 'Submissions from known data center IP range',
          affectedLeads: Math.floor(Math.random() * 5) + 1
        }
      ]

      const randomAlert = alertTypes[Math.floor(Math.random() * alertTypes.length)]
      addAlert(randomAlert)
    }, 5000)

    return () => clearInterval(interval)
  }, [addAlert])

  return (
    <AlertContext.Provider value={{
      alerts,
      activeAlerts,
      alertHistory,
      config,
      addAlert,
      dismissAlert,
      acknowledgeAlert,
      clearAllAlerts,
      updateConfig,
      playAlertSound
    }}>
      {children}
    </AlertContext.Provider>
  )
}